<template>
  <v-app>
    <v-layout>
      <!-- App Bar -->
      <v-app-bar color="primary" density="compact">
        <v-app-bar-nav-icon variant="text" @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
        <v-app-bar-title>Qualisan-RH</v-app-bar-title>
        <v-spacer></v-spacer>
        <v-btn v-if="isAuthenticated" icon @click="goToNotifications">
          <v-badge :content="unreadNotificationsCount" :value="unreadNotificationsCount" color="error">
            <v-icon>mdi-bell</v-icon>
          </v-badge>
        </v-btn>
        <v-btn v-if="isAuthenticated" icon @click="userMenu = !userMenu">
          <v-icon>mdi-account-circle</v-icon>
        </v-btn>
        <v-menu v-model="userMenu" :close-on-content-click="false" location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-card min-width="200">
            <v-list>
              <v-list-item v-if="isAuthenticated">
                <template v-slot:prepend>
                  <v-icon>mdi-account</v-icon>
                </template>
                <v-list-item-title>{{ currentUser?.first_name }} {{ currentUser?.last_name }}</v-list-item-title>
                <v-list-item-subtitle>{{ currentUser?.email }}</v-list-item-subtitle>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item v-if="isAuthenticated" link to="/profile">
                <template v-slot:prepend>
                  <v-icon>mdi-account-cog</v-icon>
                </template>
                <v-list-item-title>Mon profil</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isAuthenticated" link @click="logout">
                <template v-slot:prepend>
                  <v-icon>mdi-logout</v-icon>
                </template>
                <v-list-item-title>Déconnexion</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-app-bar>

      <!-- Navigation Drawer -->
      <v-navigation-drawer v-model="drawer" temporary>
        <v-list-item>
          <v-list-item-title class="text-h6">Qualisan-RH</v-list-item-title>
          <v-list-item-subtitle>Gestion des demandes</v-list-item-subtitle>
        </v-list-item>

        <v-divider></v-divider>

        <v-list density="compact" nav>
          <v-list-item v-if="!isAuthenticated" prepend-icon="mdi-login" title="Connexion" to="/login"></v-list-item>
          <template v-if="isAuthenticated">
            <v-list-item prepend-icon="mdi-view-dashboard" title="Tableau de bord" to="/dashboard"></v-list-item>
            <v-list-item prepend-icon="mdi-account-cog" title="Mon profil" to="/profile"></v-list-item>
            <v-list-item prepend-icon="mdi-format-list-bulleted" title="Mes demandes" to="/requests"></v-list-item>
            <v-list-item prepend-icon="mdi-plus-circle" title="Nouvelle demande" to="/requests/new"></v-list-item>
            <v-list-item prepend-icon="mdi-check-circle" title="À approuver" to="/approvals"></v-list-item>
            <v-list-item v-if="isManager" to="/processed-requests" prepend-icon="mdi-check-all">
              Demandes traitées
            </v-list-item>
            <v-list-item v-if="isManager" to="/expenses-summary" prepend-icon="mdi-cash-multiple">
              Récapitulatif des frais
            </v-list-item>
            <v-list-item prepend-icon="mdi-bell" title="Notifications" to="/notifications"></v-list-item>
            <v-list-item v-if="isAdmin" prepend-icon="mdi-account-multiple" title="Utilisateurs" to="/users"></v-list-item>
          </template>
        </v-list>
      </v-navigation-drawer>

      <!-- Main Content -->
      <v-main>
        <v-container fluid>
          <router-view />
        </v-container>
      </v-main>
    </v-layout>
  </v-app>
</template>

<script>
import { useAuthStore } from './store/auth'
import { useNotificationStore } from './store/notification'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'App',
  setup() {
    const drawer = ref(false)
    const userMenu = ref(false)
    const authStore = useAuthStore()
    const notificationStore = useNotificationStore()
    const router = useRouter()

    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const currentUser = computed(() => authStore.user)
    const isAdmin = computed(() => currentUser.value?.role === 'admin')
    const isManager = computed(() => currentUser.value?.role === 'admin' || currentUser.value?.role === 'manager')
    const unreadNotificationsCount = computed(() => notificationStore.unreadCount)

    // Référence pour l'intervalle de rafraîchissement des notifications
    let notificationRefreshInterval = null
    
    onMounted(async () => {
      if (isAuthenticated.value) {
        // Charger immédiatement le compteur de notifications non lues
        await notificationStore.fetchUnreadCount()
        
        // Configurer un intervalle pour rafraîchir le compteur toutes les 30 secondes
        notificationRefreshInterval = setInterval(async () => {
          if (isAuthenticated.value) {
            await notificationStore.fetchUnreadCount()
          }
        }, 30000) // 30 secondes
      }
    })
    
    // Nettoyer l'intervalle lorsque le composant est détruit
    onUnmounted(() => {
      if (notificationRefreshInterval) {
        clearInterval(notificationRefreshInterval)
      }
    })

    const logout = async () => {
      await authStore.logout()
      userMenu.value = false
      router.push('/login')
    }

    const goToNotifications = () => {
      router.push('/notifications')
    }

    return {
      drawer,
      userMenu,
      isAuthenticated,
      currentUser,
      isAdmin,
      isManager,
      unreadNotificationsCount,
      logout,
      goToNotifications
    }
  }
}
</script>

<style>
.v-application {
  font-family: 'Roboto', sans-serif;
}
</style>
